<template>
  <!-- Floating feedback button -->
  <div class="feedback-widget">
    <v-btn
      v-if="!open"
      class="feedback-trigger"
      color="primary"
      variant="flat"
      size="small"
      prepend-icon="mdi-message-plus-outline"
      @click="open = true"
      rounded="pill"
    >
      Feedback
    </v-btn>

    <v-card
      v-if="open"
      class="feedback-panel"
      rounded="lg"
      elevation="8"
      width="320"
    >
      <v-card-title class="d-flex align-center pa-3 pb-2">
        <v-icon size="18" class="mr-2" color="primary">mdi-message-plus-outline</v-icon>
        <span class="text-body-2 font-weight-bold">Share Feedback</span>
        <v-spacer />
        <v-btn icon="mdi-close" size="x-small" variant="text" @click="open = false" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-3" v-if="!submitted">
        <!-- Type selector -->
        <div class="type-selector mb-3">
          <button
            class="type-btn"
            :class="{ active: form.type === 'idea' }"
            @click="form.type = 'idea'"
          >
            <v-icon size="16">mdi-lightbulb-outline</v-icon>
            Idea
          </button>
          <button
            class="type-btn"
            :class="{ active: form.type === 'bug' }"
            @click="form.type = 'bug'"
          >
            <v-icon size="16">mdi-bug-outline</v-icon>
            Bug
          </button>
        </div>

        <v-text-field
          v-model="form.title"
          :label="form.type === 'idea' ? 'What\'s your idea?' : 'What went wrong?'"
          variant="outlined"
          density="compact"
          class="mb-2"
          :placeholder="form.type === 'idea' ? 'e.g. Add dark mode...' : 'e.g. Login fails when...'"
        />
        <v-textarea
          v-model="form.detail"
          label="More detail (optional)"
          variant="outlined"
          density="compact"
          rows="3"
          class="mb-3"
        />

        <v-alert v-if="error" type="error" density="compact" variant="tonal" class="mb-2 text-caption">
          {{ error }}
        </v-alert>

        <v-btn
          color="primary"
          variant="flat"
          block
          size="small"
          :loading="submitting"
          :disabled="!form.title.trim()"
          @click="submit"
        >
          {{ form.type === 'idea' ? 'Submit Idea' : 'Report Bug' }}
        </v-btn>
      </v-card-text>

      <!-- Success state -->
      <v-card-text v-else class="text-center pa-6">
        <v-icon size="48" :color="form.type === 'idea' ? 'purple' : 'error'" class="mb-3">
          {{ form.type === 'idea' ? 'mdi-lightbulb-on-outline' : 'mdi-check-circle-outline' }}
        </v-icon>
        <p class="text-body-2 font-weight-bold mb-1">{{ successMessage }}</p>
        <p class="text-caption text-medium-emphasis">We'll review it shortly.</p>
        <v-btn size="small" variant="text" class="mt-2" @click="reset">Send another</v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import api from '../services/api'

const open = ref(false)
const submitted = ref(false)
const submitting = ref(false)
const error = ref('')
const successMessage = ref('')

const form = reactive({
  type: 'idea',
  title: '',
  detail: '',
})

async function submit() {
  error.value = ''
  submitting.value = true
  try {
    const { data } = await api.post('/contact', {
      type: form.type,
      title: form.title,
      detail: form.detail,
    })
    successMessage.value = data.message
    submitted.value = true
  } catch (e) {
    error.value = e?.response?.data?.error || 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}

function reset() {
  submitted.value = false
  form.title = ''
  form.detail = ''
  error.value = ''
}
</script>

<style scoped>
.feedback-widget {
  position: fixed;
  bottom: 64px;
  left: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.feedback-trigger {
  box-shadow: 0 4px 16px rgba(0,0,0,0.2) !important;
}
.feedback-panel {
  box-shadow: 0 8px 32px rgba(0,0,0,0.25) !important;
}
.type-selector {
  display: flex;
  gap: 8px;
}
.type-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.7);
  transition: all 0.15s;
}
.type-btn.active {
  border-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}
</style>